import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Shield,
  Users,
  Building2,
  TrendingUp,
  DollarSign,
  Gift,
  Clock,
  Activity,
  ArrowRight,
} from "lucide-react";
import { adminApi } from "../../lib/api";

const SPONSOR_TYPES = [
  ["", "All types"],
  ["church", "Church"],
  ["christian_retailer", "Christian Retailer"],
  ["bookstore", "Bookstore / Gift Store"],
  ["ministry", "Ministry"],
  ["school", "Christian School"],
  ["nonprofit", "Nonprofit"],
  ["publisher", "Publisher / Media"],
  ["conference", "Conference / Event"],
  ["corporate_partner", "Corporate Partner"],
  ["other", "Other"],
];

const RANGE_OPTIONS = [
  ["today", "Today"],
  ["7d", "7 days"],
  ["30d", "30 days"],
  ["90d", "90 days"],
  ["", "All time"],
];

export default function AdminDashboardPage() {
  const [kpis, setKpis] = useState(null);
  const [eco, setEco] = useState(null);
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [range, setRange] = useState("30d");
  const [sponsorType, setSponsorType] = useState("");

  useEffect(() => {
    adminApi.kpis().then(setKpis).catch((e) => setErr(e?.response?.data?.detail || "Unable to load KPIs"));
    adminApi.ecosystem().then(setEco).catch(() => {});
  }, []);

  useEffect(() => {
    adminApi
      .performance({ range: range || undefined, sponsor_type: sponsorType || undefined })
      .then(setRows)
      .catch((e) => setErr(e?.response?.data?.detail || "Unable to load sponsors"));
  }, [range, sponsorType]);

  const mrrDollars = useMemo(
    () => (kpis ? (kpis.monthly_recurring_revenue_cents / 100).toFixed(2) : "—"),
    [kpis]
  );

  const backend = process.env.REACT_APP_BACKEND_URL;
  const token = typeof window !== "undefined" ? localStorage.getItem("lg_token") : null;

  const downloadCsv = async () => {
    const url = `${backend}/api/admin/export.csv${range ? "?range=" + range : ""}`;
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "sponsor-performance.csv";
    a.click();
  };

  return (
    <div data-testid="admin-dashboard-page" className="space-y-8 fade-in-up">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Shield size={16} className="text-[#4F6F52]" />
          <span className="text-xs uppercase tracking-[0.18em] font-semibold text-[#4F6F52]">
            Master Admin
          </span>
        </div>
        <h1 className="font-display text-4xl">Sponsor Analytics Console</h1>
        <p className="text-sm text-[#6B6B6B] mt-1">
          System-wide LifeGuide KPIs, sponsor performance, and attribution funnel.
        </p>
      </div>

      {err && (
        <div className="rounded-2xl border border-[#D9BEB5] bg-[#F7ECE8] text-[#7C3E33] p-4 text-sm">
          {err}
        </div>
      )}

      {/* -- KPI grid ---------------------------------------------------- */}
      <section>
        <h2 className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#4F6F52] mb-3">
          LifeGuide KPIs
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          <Kpi icon={Users} label="Total users" value={kpis?.total_users} testid="kpi-total-users" />
          <Kpi icon={TrendingUp} label="New — 7 days" value={kpis?.new_users_7d} testid="kpi-new-7d" />
          <Kpi icon={TrendingUp} label="New — 30 days" value={kpis?.new_users_30d} testid="kpi-new-30d" />
          <Kpi icon={Activity} label="Active — 7 days" value={kpis?.active_users_7d} testid="kpi-active-7d" />
          <Kpi icon={Activity} label="Active — 30 days" value={kpis?.active_users_30d} testid="kpi-active-30d" />
          <Kpi icon={Users} label="Free users" value={kpis?.free_users} testid="kpi-free" />
          <Kpi icon={Clock} label="Pro trials" value={kpis?.trial_users} testid="kpi-trials" />
          <Kpi icon={DollarSign} label="Paid Pro" value={kpis?.paid_pro} testid="kpi-paid" />
          <Kpi icon={Gift} label="Gift subscriptions" value={kpis?.gift_subscriptions} testid="kpi-gifts" />
          <Kpi icon={DollarSign} label="MRR" value={kpis ? `$${mrrDollars}` : "—"} testid="kpi-mrr" />
          <Kpi icon={Building2} label="Sponsors total" value={kpis?.total_sponsors} testid="kpi-sponsors-total" />
          <Kpi icon={Building2} label="Active sponsors" value={kpis?.active_sponsors} testid="kpi-sponsors-active" />
        </div>
      </section>

      {/* -- Ecosystem: LifeGuide + I Need a Proverb ---------------------- */}
      {eco && (
        <section data-testid="ecosystem-section" className="rounded-3xl border border-[#DCE5DB] bg-white p-6">
          <h2 className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#4F6F52] mb-3">
            Ecosystem
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-display text-lg mb-2">LifeGuide</h3>
              <ul className="text-sm space-y-1">
                <li>Total users: <strong>{eco.lifeguide.total_users}</strong></li>
                <li>New (30d): <strong>{eco.lifeguide.new_users_30d}</strong></li>
                <li>Active (30d): <strong>{eco.lifeguide.active_users_30d}</strong></li>
                <li>Paid Pro: <strong>{eco.lifeguide.paid_pro}</strong></li>
                <li>Sponsor-attributed users: <strong>{eco.lifeguide.sponsor_attributed_users}</strong></li>
              </ul>
            </div>
            <div>
              <h3 className="font-display text-lg mb-2">I Need a Proverb</h3>
              {eco.proverb.status === "awaiting_integration" ? (
                <div
                  data-testid="proverb-awaiting"
                  className="rounded-2xl border border-dashed border-[#DCE5DB] p-4 text-sm text-[#6B6B6B] italic"
                >
                  Awaiting I Need a Proverb analytics integration.
                </div>
              ) : (
                <ul className="text-sm space-y-1">
                  <li>Visitors: <strong>{eco.proverb.visitors}</strong></li>
                  <li>Searches: <strong>{eco.proverb.searches}</strong></li>
                  <li>Results viewed: <strong>{eco.proverb.results_viewed}</strong></li>
                  <li>Shares: <strong>{eco.proverb.shares}</strong></li>
                  <li>LifeGuide clicks: <strong>{eco.proverb.lifeguide_clicks}</strong></li>
                  <li>LifeGuide referrals: <strong>{eco.proverb.lifeguide_referrals}</strong></li>
                  <li>LifeGuide conversions: <strong>{eco.proverb.lifeguide_conversions}</strong></li>
                </ul>
              )}
            </div>
          </div>
          <div className="mt-5 border-t border-[#DCE5DB] pt-4">
            <h3 className="text-[11px] uppercase tracking-[0.14em] text-[#4F6F52] font-semibold mb-2">
              Cross-product funnel
            </h3>
            <div className="grid grid-cols-5 gap-2 text-xs text-center">
              {[
                ["Proverb visitors", eco.funnel.proverb_visitors],
                ["LifeGuide clicks", eco.funnel.lifeguide_clicks],
                ["Signups", eco.funnel.signups_from_proverb],
                ["Trials", eco.funnel.trials_from_proverb],
                ["Paid Pro", eco.funnel.paid_from_proverb],
              ].map(([lbl, v], i) => (
                <div key={i} className="rounded-xl bg-[#F5F7F2] p-2">
                  <div className="text-[#6B6B6B]">{lbl}</div>
                  <div className="text-lg font-display mt-1">
                    {v === null ? <span className="text-[#6B6B6B] italic text-sm">—</span> : v}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* -- Filters + Sponsor table ------------------------------------ */}
      <section className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="font-display text-2xl">Sponsor performance</h2>
            <p className="text-xs text-[#6B6B6B] mt-1">
              Click a sponsor to open its dashboard.
            </p>
          </div>
          <div className="flex gap-2">
            <label className="block">
              <div className="text-xs text-[#6B6B6B] mb-1">Range</div>
              <select
                data-testid="admin-range-filter"
                value={range}
                onChange={(e) => setRange(e.target.value)}
                className="bg-white border border-[#DCE5DB] rounded-2xl px-3 py-2 text-sm"
              >
                {RANGE_OPTIONS.map(([v, l]) => (
                  <option key={v || "all"} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <div className="text-xs text-[#6B6B6B] mb-1">Sponsor type</div>
              <select
                data-testid="admin-type-filter"
                value={sponsorType}
                onChange={(e) => setSponsorType(e.target.value)}
                className="bg-white border border-[#DCE5DB] rounded-2xl px-3 py-2 text-sm"
              >
                {SPONSOR_TYPES.map(([v, l]) => (
                  <option key={v || "all"} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </label>
            <Link
              to="/admin/sponsors"
              data-testid="admin-manage-sponsors"
              className="inline-flex items-end gap-1.5 text-sm text-[#4F6F52] hover:text-[#3F5A42] pb-2"
            >
              Manage <ArrowRight size={14} />
            </Link>
            <button
              data-testid="admin-download-csv"
              onClick={downloadCsv}
              className="inline-flex items-end gap-1.5 text-sm text-[#4F6F52] hover:text-[#3F5A42] pb-2 underline underline-offset-2"
            >
              Download CSV
            </button>
          </div>
        </div>

        <div className="rounded-3xl border border-[#DCE5DB] bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F7F2] text-[#4F6F52] text-[11px] uppercase tracking-[0.14em]">
              <tr>
                <th className="text-left px-4 py-3">Sponsor</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-right px-4 py-3">Audience</th>
                <th className="text-right px-4 py-3">Visits</th>
                <th className="text-right px-4 py-3">Clicks</th>
                <th className="text-right px-4 py-3">Signups</th>
                <th className="text-right px-4 py-3">Active</th>
                <th className="text-right px-4 py-3">Trials</th>
                <th className="text-right px-4 py-3">Paid</th>
                <th className="text-right px-4 py-3">Gifts</th>
                <th className="text-right px-4 py-3">Conv %</th>
                <th className="text-right px-4 py-3">Adoption %</th>
                <th className="text-left px-4 py-3">Last activity</th>
                <th className="text-left px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows === null && (
                <tr>
                  <td className="px-4 py-6 text-[#6B6B6B]" colSpan={14}>
                    Loading…
                  </td>
                </tr>
              )}
              {rows && rows.length === 0 && (
                <tr>
                  <td
                    data-testid="admin-empty-row"
                    className="px-4 py-6 text-[#6B6B6B] text-center"
                    colSpan={14}
                  >
                    No sponsors match this filter.
                  </td>
                </tr>
              )}
              {rows &&
                rows.map((r) => (
                  <tr
                    key={r.sponsor_id}
                    data-testid={`admin-perf-row-${r.sponsor_slug}`}
                    className="border-t border-[#DCE5DB]/60 hover:bg-[#EEF3EC]/40"
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={`/sponsor-dashboard/${r.sponsor_id}`}
                        className="text-[#4F6F52] hover:text-[#3F5A42] font-medium"
                      >
                        {r.sponsor_name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-[#6B6B6B]">{r.sponsor_type}</td>
                    <td className="px-4 py-3 text-right">{r.audience_size ?? "—"}</td>
                    <td className="px-4 py-3 text-right">{r.landing_visits}</td>
                    <td className="px-4 py-3 text-right">{r.referral_clicks}</td>
                    <td className="px-4 py-3 text-right">{r.signups}</td>
                    <td className="px-4 py-3 text-right">{r.active_users}</td>
                    <td className="px-4 py-3 text-right">{r.trials}</td>
                    <td className="px-4 py-3 text-right">{r.paid}</td>
                    <td className="px-4 py-3 text-right">{r.gifts}</td>
                    <td className="px-4 py-3 text-right">{r.conversion_rate}%</td>
                    <td className="px-4 py-3 text-right">
                      {r.active_adoption_rate == null ? (
                        <span className="text-[#6B6B6B] italic text-xs">Not available</span>
                      ) : (
                        `${r.active_adoption_rate}%`
                      )}
                    </td>
                    <td className="px-4 py-3 text-[#6B6B6B]">
                      {r.last_activity ? r.last_activity.slice(0, 10) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded-full ${
                          r.status === "active"
                            ? "bg-[#E1ECDD] text-[#4F6F52]"
                            : "bg-[#F0EDE7] text-[#6B6B6B]"
                        }`}
                      >
                        {r.status}
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-[#DCE5DB] bg-white p-4"
    >
      <div className="flex items-center gap-2 text-[#4F6F52] mb-2">
        <Icon size={13} />
        <span className="text-[10px] uppercase tracking-[0.14em] font-semibold">
          {label}
        </span>
      </div>
      <div className="text-2xl font-display">{value ?? "—"}</div>
    </div>
  );
}
